/**
 * useStableBlobUrl — keep blob URLs byte-identical across polls so the browser
 * cache can re-use the underlying bytes even when the server re-signs the SAS.
 *
 * The substrate / .NET API sometimes emit a fresh `?sig=…` for the same blob
 * on each poll. From the browser's perspective every poll looks like a brand-
 * new resource, so the image is re-downloaded even when the bytes are
 * unchanged. This helper short-circuits that:
 *
 *   1. Key the URL by its host + path (ignore the query — the SAS is what
 *      rotates). Same convention as the substrate-side cache key in
 *      `workflows/rpa/blob_sas.py:_cache_key`.
 *   2. Parse the SAS `se=` expiry. If the cached URL still has > 60 s of
 *      lifetime, return it instead of the freshly-arrived one. Browsers then
 *      see byte-identical URLs across polls and serve from the HTTP cache.
 *   3. When a URL is missing `se=` (non-SAS URL), or its expiry is past, or
 *      it's about to expire, drop the cache entry and adopt the new URL.
 *
 * Belt-and-braces partner to the server-side `BlobResultResigner` fix that
 * reuses healthy SAS tokens. Either change alone partially solves the
 * SAS-rotation cache-bust; together they make image bytes flow exactly once
 * per ~14-minute SAS window.
 */

import { useMemo } from "react";

interface CacheEntry {
  url: string;
  expiresAt: number; // epoch ms
}

// Module-level cache shared across all components. Map preserves insertion
// order which we use for opportunistic eviction.
const cache = new Map<string, CacheEntry>();
const MAX_ENTRIES = 1024;
const SAFETY_MARGIN_MS = 60_000;

function extractKey(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl);
    return `${u.host}${u.pathname}`;
  } catch {
    return null;
  }
}

function extractExpiry(rawUrl: string): number | null {
  try {
    const u = new URL(rawUrl);
    const se = u.searchParams.get("se");
    if (!se) return null;
    const ms = Date.parse(se);
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

function stabilize(rawUrl: string, now: number): string {
  const key = extractKey(rawUrl);
  if (!key) return rawUrl;

  const cached = cache.get(key);
  if (cached && cached.expiresAt - SAFETY_MARGIN_MS > now) {
    // Cached URL still healthy — return it so the browser sees a stable URL.
    return cached.url;
  }

  // Either no cache entry or the cached entry is about to expire. Adopt the
  // fresh URL if it has its own healthy expiry.
  const expiresAt = extractExpiry(rawUrl);
  if (expiresAt && expiresAt - SAFETY_MARGIN_MS > now) {
    cache.set(key, { url: rawUrl, expiresAt });
    if (cache.size > MAX_ENTRIES) {
      const firstKey = cache.keys().next().value;
      if (firstKey !== undefined) cache.delete(firstKey);
    }
  } else if (cached) {
    // Fresh URL has no usable expiry either — drop the stale cache entry so
    // the next call doesn't keep serving a dead URL.
    cache.delete(key);
  }
  return rawUrl;
}

/**
 * Return a stable URL for `rawUrl`. Memoized so the same input within one
 * render returns the same string identity (handy for React's `key` and
 * dependency-array checks).
 */
export function useStableBlobUrl(
  rawUrl: string | null | undefined,
): string | null | undefined {
  return useMemo(() => {
    if (!rawUrl) return rawUrl;
    return stabilize(rawUrl, Date.now());
  }, [rawUrl]);
}

/**
 * Stabilize an array of URLs together. Used when a payload contains a list
 * of artifacts (`screenshot_uri[]`, attachment URLs) and we want all of them
 * to flip atomically when polling refreshes them.
 */
export function useStableBlobUrls(
  rawUrls: ReadonlyArray<string | null | undefined>,
): Array<string | null | undefined> {
  return useMemo(() => {
    const now = Date.now();
    return rawUrls.map((u) => (u ? stabilize(u, now) : u));
  }, [rawUrls]);
}

/** Test-only helper. Production code never calls this. */
export function resetStableBlobUrlCacheForTests(): void {
  cache.clear();
}
